import {gsap} from "gsap";
import {MathHelper} from "./MathHelper";

export interface AttentionConfig {
    playSweep?: boolean;
    playDots?: boolean;
    duration?: number;
    frameMaxAlpha?: number;
    sweepMaxAlpha?: number;
    tintColor: number;
}

export interface WaysConfig {
    duration?: number;
    frameMaxAlpha?: number;
    tintColor: number;
}

export interface PreWinConfig {
    duration?: number;
    frameMaxAlpha?: number;
    tintColor: number;
}

export interface WinConfig {
    centerLine?: boolean;
    duration?: number;
    frameMaxAlpha?: number;
    tintColor: number;
}

export class GlowFrame extends PIXI.Container {
    public frameSize: PIXI.ObservablePoint;
    public spread: PIXI.ObservablePoint;
    private _frame: PIXI.NineSlicePlane;
    private _dots: PIXI.Sprite[];
    private _container: PIXI.Container;
    private _corners: PIXI.Point[];
    private _dirty: boolean = true;
    private _sweep: PIXI.Sprite;
    private _sweepMask: PIXI.Graphics;
    private _centers: PIXI.Point[];
    private _centerLine: PIXI.Sprite;

    private _isActive:boolean;
    private _showTL:gsap.core.Timeline;
    private _hideTL:gsap.core.Timeline;
    private _preWinHighlightTL:gsap.core.Timeline;
    private _winHighlightTL:gsap.core.Timeline;

    constructor(frameSprite: PIXI.NineSlicePlane, dotSpites: PIXI.Sprite[], sweepSprite: PIXI.Sprite, horizontalLineSprite: PIXI.Sprite) {
        super();

        this.visible = false;

        this._sweep = sweepSprite;
        this._sweep.name = "_sweep";
        this._sweep.blendMode = PIXI.BLEND_MODES.ADD;
        this._sweep.alpha = 0;
        this._sweepMask = new PIXI.Graphics();
        this._sweepMask.name = "_sweepMask";
        this._sweep.mask = this._sweepMask;

        this._frame = frameSprite;
        this._frame.name = "_frame";
        this._frame.blendMode = PIXI.BLEND_MODES.ADD;
        this._frame.alpha = 0;

        this._dots = dotSpites;
        for (let i: number = 0; i < this._dots.length; i++) {
            this._dots[i].blendMode = PIXI.BLEND_MODES.ADD;
            this._dots[i].anchor.set(0.5, 0.5);
            this._dots[i].alpha = 0;
            this._dots[i].name = "dot_" + i;
        }

        this._centerLine = horizontalLineSprite;
        this._centerLine.name = "_centerLine";
        this._centerLine.anchor.set(0.5, 0.5);
        this._centerLine.blendMode = PIXI.BLEND_MODES.ADD;
        this._centerLine.alpha = 0;

        this._corners = [];

        this._container = new PIXI.Container();
        this._container.addChild(this._sweepMask);
        this._container.addChild(this._sweep);
        this._container.addChild(this._frame);
        this._container.addChild(this._centerLine);
        this._container.addChild(...this._dots);

        this.addChild(this._container);

        this.frameSize = new PIXI.ObservablePoint(this.onFrameSize, this);
        this.spread = new PIXI.ObservablePoint(this.onSpread, this);
    }

    public set tint(value: number) {
        this._frame.tint = value;
        this._sweep.tint = value;
        this._centerLine.tint = value;
        for (let dot of this._dots) {
            dot.tint = value;
        }
    }

    public onSpread(): void {
        this._container.pivot.set(this.spread.x, this.spread.y);
        this._dirty = true;
    }

    public onFrameSize(): void {
        this._frame.width = this.frameSize.x + this.spread.x * 2;
        this._frame.height = this.frameSize.y + this.spread.y * 2;

        this._centerLine.position.set(this._frame.width * 0.5, this._frame.height * 0.5);
        this._dirty = true;
    }

    public show(duration: number = 0): gsap.core.Timeline {
        if(this._isActive){
            this._showTL.progress(1);
            this._preWinHighlightTL.progress(1);
            this._hideTL.progress(1);

            this._showTL.kill();
            this._preWinHighlightTL.kill();
            this._hideTL.kill();
        }

        if (this._dirty) {
            this.reCalcCorners();
        }
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this._isActive=true;
            this.visible = true;
        });
        if (duration == 0) {
            tl.add(()=>{
                this.alpha=1;
            })
            // tl.add(gsap.set(this, {alpha: 1}));
        } else {
            tl.to(this, {duration: duration, alpha: 1, ease: "none"});
        }
        this._showTL=tl;
        return this._showTL;
    }

    public hide(duration: number = 0): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        if (duration == 0) {
            tl.add(gsap.to(this, {duration: 0.001, alpha: 0}));
        } else {
            tl.add(gsap.to(this, {duration: duration, alpha: 0, ease: "none"}));
        }
        tl.add(() => {
            this.reset();
        });
        this._hideTL=tl;
        return this._hideTL;
    }

    public getPreWinAnimation(config: PreWinConfig): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
            onComplete:()=>{
                // console.log(this.alpha,"winHighlight")
            }
            }),
            animDuration: number = config.duration ? config.duration : 1,
            frameOpacity: number = config.frameMaxAlpha ? config.frameMaxAlpha : 1;
        tl.add(()=>{
            this.alpha = frameOpacity;
            this.tint = config.tintColor;
        });
        tl.add(gsap.to(this._frame, {duration: (animDuration * 0.05), alpha: 1.0, ease: "none"}));
        tl.add(this.getEdgeDots((animDuration * 0.7)), "<");
        tl.add(gsap.to(this._frame, {duration: (animDuration * 0.2), alpha: 0.5, ease: "none"}), ">-0.2");
        this._preWinHighlightTL=tl;
        return this._preWinHighlightTL;
    }

    public reset(): void {
        this._frame.alpha = 0;
        this._sweep.alpha = 0;
        this._centerLine.alpha = 0;
        for (let i: number = 0; i < this._dots.length; i++) {
            this._dots[i].alpha = 0;
            this._dots[i].scale.set(1, 1);
        }
        this.visible = false;
        this._isActive=false;
    }

    private createTimelineFromArray(children: any[], position?: gsap.Position): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        for (let child of children) {
            tl.add(child, position);
        }
        return tl;
    }

    private reCalcCorners(): void {
        //top-left, top-right, bottom-right, bottom-left.
        this._corners = [
            new PIXI.Point(this.spread.x, this.spread.y),
            new PIXI.Point(this._frame.width - this.spread.x, this.spread.y),
            new PIXI.Point(this._frame.width - this.spread.x, this._frame.height - this.spread.y),
            new PIXI.Point(this.spread.x, this._frame.height - this.spread.y),
        ];

        //top center , right center, bottom center, left center,
        this._centers = [
            new PIXI.Point(this._frame.width * 0.5, this.spread.y),
            new PIXI.Point(this._frame.width, this._frame.height * 0.5),
            new PIXI.Point(this._frame.width * 0.5, this._frame.height),
            new PIXI.Point(this.spread.x, this._frame.height * 0.5),
        ]

        this._sweepMask.clear();
        this._sweepMask.beginFill();
        this._sweepMask.drawRect(this.spread.x, this.spread.y, this.frameSize.x, this.frameSize.y);
        this._sweepMask.endFill();

        this.setDotRotation()
    }

    private setDotRotation(): void {
        this._dots[0].rotation = MathHelper.angleBetweenPoints(this._corners[0], this._corners[1]);
        this._dots[1].rotation = MathHelper.angleBetweenPoints(this._corners[1], this._corners[2]);
        this._dots[2].rotation = MathHelper.angleBetweenPoints(this._corners[2], this._corners[3]);
        this._dots[3].rotation = MathHelper.angleBetweenPoints(this._corners[3], this._corners[0]);
    }

    private getDotMoveAnimation(dot: PIXI.Sprite, start: PIXI.Point, end: PIXI.Point, horizontal: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            dot.position.set(start.x, start.y);
        });
        const maxScale = horizontal ? this.frameSize.x / 50 : this.frameSize.y / 50;

        tl.add(gsap.to(dot, {x: end.x, y: end.y, duration: 1, ease: "none"}));
        tl.add(gsap.to(dot.scale, {x: maxScale, duration: 0.5, ease: "none"}), "<");
        tl.add(gsap.to(dot.scale, {x: 1, duration: 0.5, ease: "none"}), ">");

        tl.add(gsap.to(dot, {duration: 0.2, alpha: 1, ease: "none"}), 0);
        tl.add(gsap.to(dot, {duration: 0.2, alpha: 0, ease: "none"}), "-=0.2");

        return tl;
    }

    private getEdgeDots(duration: number = 1, edgeGsapPositions: gsap.Position = "<"): gsap.core.Timeline {
        const tl: gsap.core.Timeline = this.createTimelineFromArray([
            this.getDotMoveAnimation(this._dots[0], this._corners[0], this._corners[1], true),
            this.getDotMoveAnimation(this._dots[1], this._corners[1], this._corners[2], false),
            this.getDotMoveAnimation(this._dots[2], this._corners[2], this._corners[3], true),
            this.getDotMoveAnimation(this._dots[3], this._corners[3], this._corners[0], false),
        ], edgeGsapPositions);

        tl.duration(duration);
        return tl;
    }
    get isActive(): boolean {
        return this._isActive;
    }
}